import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { Reveal, EASE } from "@/lib/motion";
import { PageHero, PrimaryCTAs, SectionHeader } from "@/components/site/PageParts";
import { CTABand } from "@/components/site/Bands";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { SEO, breadcrumbSchema, howToSchema } from "@/components/site/SEO";

const CRUMBS = [
  { name: "Home", path: "/" },
  { name: "How We Work", path: "/how-we-work" },
];

const PRINCIPLES = [
  { n: "01", title: "Strategy Before Execution", desc: "We never write a word without understanding your buyers, competitors, and goals." },
  { n: "02", title: "Quality Through Systems", desc: "Consistent quality comes from repeatable processes, not from hoping writers 'get it right.'" },
  { n: "03", title: "Compounding by Design", desc: "Every piece is built to keep working — ranking, getting cited, and feeding the next piece — long after it's published." },
];

const STEPS = [
  { n: "01", title: "Strategic Foundation", body: "We gather deep buyer insights, analyze competitor gaps, and build a universe of 500–1,000 buyer-led keywords organized into 20+ topic clusters mapped to your buyer journey." },
  { n: "02", title: "Content Architecture", body: "We audit your existing content, identify strategic gaps, and assign the right format to each cluster — pillar pages, guides, comparisons, or tools — based on what actually ranks." },
  { n: "03", title: "Multi-Channel Roadmap", body: "We build your content calendar, prioritizing clusters by impact vs. effort, with every piece engineered for SEO, AEO, and GEO from day one." },
  { n: "04", title: "Plant the Seeds", body: "We publish the first wave of high-priority pieces optimized for Google rankings and AI citations, establish the operations workflow, and set the AI citation tracking baseline." },
  { n: "05", title: "Roots & Growth", body: "We publish pillar content that builds category authority, create the internal linking architecture that compounds ranking power, and connect the full content ecosystem — the phase where the first AI citations and measurable pipeline impact appear." },
  { n: "06", title: "Compounding Effect", body: "Content spreads across new AI surfaces without extra work, Google authority compounds so new pieces rank faster, your sales team uses content in the majority of deals, and competitors can't catch up." },
];

const CHECKLIST = [
  "Grammarly review",
  "Hemingway Grade check",
  "Meta description (per client requirement)",
  "Media files/links checked",
  "External links checked",
  "Paragraph sizing (never more than 5 lines)",
  "Plagiarism detection",
  "AI content detection",
];

export default function HowWeWork() {
  return (
    <div data-testid="how-we-work-page">
      <SEO
        title="How We Work — The Dandelion Method"
        description="A differentiated content approach that compounds across Google, ChatGPT, and Perplexity. Six repeatable steps, a real QC checklist, and the tools we use."
        path="/how-we-work"
        jsonLd={[
          breadcrumbSchema(CRUMBS),
          howToSchema({
            name: "The Dandelion Method",
            description: "Upword Media's six-step framework for B2B SaaS content that ranks in Google, gets cited by AI search, and compounds over time.",
            steps: STEPS.map((s) => ({ name: s.title, text: s.body })),
          }),
        ]}
      />
      <PageHero
        eyebrow="How we work"
        lines={["The Dandelion"]}
        italicLast="Method."
        sub="A Differentiated Approach To Content That Compounds. We plant strategic seeds that spread across Google, ChatGPT, Perplexity, and your entire buyer journey."
        crumbs={CRUMBS}
        accent
      >
        <PrimaryCTAs />
      </PageHero>

      {/* Principles */}
      <section className="section-pad bg-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <SectionHeader eyebrow="Process philosophy" title="Three principles behind every piece we ship." />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 border-t border-l border-[#E0E0E0]">
            {PRINCIPLES.map((p, i) => (
              <Reveal key={p.n} delay={i * 0.08}>
                <div className="p-8 md:p-10 border-r border-b border-[#E0E0E0] h-full">
                  <span className="font-mono text-xs text-[#999999]">{p.n}</span>
                  <h3 className="mt-5 font-serif text-2xl md:text-3xl text-[#111111]">{p.title}</h3>
                  <p className="mt-4 text-[#666666] leading-relaxed">{p.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Dandelion 6 steps as accordion */}
      <section className="section-pad bg-[#F5F5F5]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <SectionHeader eyebrow="The 6-step method" title="How a Dandelion Method engagement runs." />
          <Reveal delay={0.1}>
            <Accordion type="single" collapsible defaultValue="s0" className="mt-14 border-t border-[#111111]" data-testid="dandelion-accordion">
              {STEPS.map((s, i) => (
                <AccordionItem key={s.n} value={`s${i}`} className="border-b border-[#E0E0E0]" id={`step-${i + 1}`}>
                  <AccordionTrigger className="py-7 text-left hover:no-underline">
                    <span className="flex items-baseline gap-6 md:gap-10 w-full pr-4">
                      <span className="font-serif text-4xl md:text-6xl text-[#111111]">{s.n}</span>
                      <span className="flex-1 font-serif text-2xl md:text-3xl text-[#111111]">{s.title}</span>
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="pb-8 pl-16 md:pl-28 pr-8 text-base md:text-lg text-[#666666] leading-relaxed">
                    {s.body}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      {/* Quality Control Checklist */}
      <section className="section-pad bg-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <SectionHeader eyebrow="Quality control" title="The checklist every piece has to pass." />
            <p className="mt-6 max-w-md text-[#666666] leading-relaxed">
              Real checklist, real steps. Not a marketing promise — it's what runs on every draft before it leaves the door.
            </p>
          </div>
          <div className="lg:col-span-7">
            <ul className="divide-y divide-[#E0E0E0] border-y border-[#E0E0E0]">
              {CHECKLIST.map((item, i) => (
                <motion.li
                  key={item}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.5, delay: i * 0.05, ease: EASE }}
                  className="flex items-center gap-5 py-4"
                >
                  <span className="w-8 h-8 border border-[#111111] flex items-center justify-center shrink-0">
                    <Check size={16} className="text-[#111111]" />
                  </span>
                  <span className="text-[#111111] text-lg">{item}</span>
                  <span className="ml-auto font-mono text-xs text-[#999999]">0{i + 1}</span>
                </motion.li>
              ))}
            </ul>
            <div className="mt-10 border-l-2 border-[#111111] pl-6">
              <p className="text-xs uppercase tracking-[0.2em] text-[#999999]">Revision policy</p>
              <p className="mt-3 text-lg text-[#111111] leading-relaxed">
                Unlimited revisions until you&apos;re satisfied. 48-hour turnaround for minor changes, 72 hours for major rewrites.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Tools */}
      <section className="section-pad bg-[#111111] text-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] text-[#666666] mb-6 font-mono">Tools we use</p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] max-w-4xl">
              We&apos;re precious about the output, not the stack.
            </h2>
            <p className="mt-8 max-w-3xl text-lg md:text-xl text-[#E0E0E0] leading-relaxed">
              Our core stack: Google Analytics, Google Search Console, Grammarly, Copyscape, SEMrush, and ClickUp.
              Beyond that, we adapt to whatever tools your team already works in — we&apos;re not precious about our stack, we&apos;re precious about the output.
            </p>
            <div className="mt-10 flex flex-wrap gap-2">
              {["Google Analytics", "Search Console", "Grammarly", "Copyscape", "SEMrush", "ClickUp"].map((t) => (
                <span key={t} className="border border-[#333333] px-4 py-2 text-sm text-[#E0E0E0]">{t}</span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <CTABand heading="Ready to plant the first seed?" showSecondary={true} showCaseStudies={false} />
    </div>
  );
}
